import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import useFixedDropdownMenu from '../../../Hooks/useFixedDropdownMenu';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import { matchesUrlName } from '../../../utils/slugify';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminDetailFieldLabel,
  AdminConfigLockedField,
  AdminFormFieldError,
  AdminFormDropdownChevron,
  AdminSupportingText,
  AdminTableBody,
  AdminTableHeader,
  AdminCompactFormPage,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminUploadFieldClasses,
  DASHBOARD_DETAIL_FIELD_WIDTH,
} from '../shared/adminUx';
import { isTeamOwnerActor } from '../../../data/teamConfig';
import { buildSampleFormInventory } from '../../../data/formInventoryConfig';
import {
  APPROVAL_REQUIREMENT_ROWS,
  ASK_MORE_INFO_FIXED_ROWS,
  CONDITION_OPERATOR_OPTIONS,
  COMPLIANCE_CONFIGURATION_ROUTE,
  EFFECTIVE_FROM_ROWS,
  REJECTION_BASIS_OPTIONS,
  buildComplianceItemsCatalogFromLbt,
  buildSampleComplianceConfigurations,
  buildSavedTradeCategoryRecords,
  createEmptyCondition,
  enrichConfigFromSource,
  findComplianceItem,
  formatComplianceDecisionFormDisplay,
  formatComplianceSourceOptionLabel,
  getComplianceTypeLabel,
  getConditionFieldsForSource,
  getDecisionFormChecklist,
  getRuleTradeCategoryLabel,
  getSourceApplicationFields,
  getUnlinkedComplianceItems,
  nextConditionId,
  operatorsForKind,
  resolveLinkedForm,
} from '../../../data/complianceConfiguration';
import {
  SAVE_FAILURE_MESSAGE,
  buildComplianceConfigurationActor,
  createEmptyComplianceConfigurationDraft,
  draftFromConfiguration,
  mergeComplianceConfigurationsAfterSave,
  ruleFingerprint,
  sourceFingerprint,
} from '../../../data/complianceConfigurationWorkflow';

const dropdownPanelClass =
  'fixed z-[200] bg-white border border-lightgray rounded-xl shadow-lg max-h-48 overflow-y-auto py-1';

const addItemLinkClass =
  'inline-flex items-center gap-1.5 text-sm font-medium text-primaryblue hover:text-brightblue';

const trashButtonClass =
  'shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred';

const TABLE_2 = 'minmax(0, 1.4fr) minmax(0, 1fr)';
const TABLE_3 = 'minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1.2fr)';

const SourceIconDisplay = ({ iconUrl, fileName }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>Icon</AdminDetailFieldLabel>
    <div
      className={`${adminUploadFieldClasses()} ${DASHBOARD_DETAIL_FIELD_WIDTH} !bg-white !min-h-[calc(var(--dashboard-field-medium-height)*3)] !h-[calc(var(--dashboard-field-medium-height)*3)] !py-2 flex flex-col items-center justify-center gap-1 text-center`}
    >
      {iconUrl ? (
        <img src={iconUrl} alt="" className="w-8 h-8 object-contain" />
      ) : (
        <i className="fi fi-rs-picture text-base text-mediumgray" aria-hidden />
      )}
      <AdminSupportingText className="mb-0">
        {fileName || (iconUrl ? 'Icon uploaded' : '—')}
      </AdminSupportingText>
    </div>
  </div>
);

const SpecTable = ({ columns, rows }) => (
  <div className="overflow-x-auto min-w-0">
    <div
      className="grid w-full py-3 text-offblack border-b border-lightgray gap-x-3"
      style={{ gridTemplateColumns: columns }}
    >
      {rows[0]?.map((cell, index) => (
        <AdminTableHeader key={`h-${index}`}>{cell}</AdminTableHeader>
      ))}
    </div>
    {rows.slice(1).map((row, rowIndex) => (
      <div
        key={`r-${rowIndex}`}
        className="grid w-full py-3 text-offblack border-b border-lightgray last:border-b-0 gap-x-3"
        style={{ gridTemplateColumns: columns }}
      >
        {row.map((cell, cellIndex) => (
          <AdminTableBody key={`c-${rowIndex}-${cellIndex}`} className="break-words min-w-0">
            {cell}
          </AdminTableBody>
        ))}
      </div>
    ))}
  </div>
);

const MultiOptionList = ({ options, values, onChange }) => (
  <div className="space-y-2">
    {(options || []).map((option) => {
      const checked = (values || []).includes(option);
      return (
        <label key={option} className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={checked}
            onChange={() => {
              const next = checked
                ? (values || []).filter((value) => value !== option)
                : [...(values || []), option];
              onChange(next);
            }}
          />
          <AdminTableBody>{option}</AdminTableBody>
        </label>
      );
    })}
  </div>
);

const useDropdownClickOutside = (open, anchorRef, menuRef, onClose) => {
  useEffect(() => {
    if (!open) return undefined;
    const listener = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [open, anchorRef, menuRef, onClose]);
};

const SearchableSelect = ({
  value,
  options,
  onChange,
  disabled,
  error,
  placeholder,
  getOptionLabel = (opt) => opt.name,
  getOptionValue = (opt) => opt.id,
}) => {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { menuRef, layout, placementReady } = useFixedDropdownMenu(open, containerRef);
  const close = () => setOpen(false);
  useDropdownClickOutside(open, containerRef, menuRef, close);

  const selected = (options || []).find((o) => getOptionValue(o) === value);
  const filtered = (options || []).filter((opt) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      String(getOptionLabel(opt) || '').toLowerCase().includes(q) ||
      String(getOptionValue(opt)).toLowerCase().includes(q)
    );
  });

  if (disabled) {
    return (
      <AdminTableBody className="py-2 break-words">
        {selected ? getOptionLabel(selected) : '—'}
      </AdminTableBody>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full min-w-0 space-y-1">
      <div className="relative w-full">
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          placeholder={placeholder}
          className={adminFormDetailInputClasses(false, Boolean(error))}
          value={open ? search : (selected ? getOptionLabel(selected) : '')}
          onFocus={() => {
            setOpen(true);
            setSearch('');
          }}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
        />
        <AdminFormDropdownChevron open={open} />
      </div>
      {open && layout && createPortal(
        <div
          ref={menuRef}
          className={`${dropdownPanelClass} ${placementReady ? '' : 'invisible pointer-events-none'}`}
          style={{ top: layout.top, left: layout.left, width: layout.width }}
        >
          <button
            type="button"
            className="w-full text-left px-3 py-2 text-offblack hover:bg-pastalBlueWhite"
            onClick={() => {
              onChange('');
              setSearch('');
              setOpen(false);
            }}
          >
            <AdminTableBody>{placeholder}</AdminTableBody>
          </button>
          {filtered.map((opt) => {
            const optValue = getOptionValue(opt);
            return (
              <button
                key={optValue}
                type="button"
                className={`w-full text-left px-3 py-2 hover:bg-pastalBlueWhite ${
                  optValue === value ? 'text-primaryblue' : 'text-offblack'
                }`}
                onClick={() => {
                  onChange(optValue);
                  setSearch('');
                  setOpen(false);
                }}
              >
                <AdminTableBody className={optValue === value ? 'text-primaryblue' : ''}>
                  {getOptionLabel(opt)}
                </AdminTableBody>
              </button>
            );
          })}
          {filtered.length === 0 ? (
            <div className="px-3 py-2">
              <AdminSupportingText className="text-mediumgray">No matching options</AdminSupportingText>
            </div>
          ) : null}
        </div>,
        document.body,
      )}
      {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
    </div>
  );
};

const ComplianceRule = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ruleName: ruleNameParam } = useParams();
  const { auth } = useAuth();
  const canAccess = isTeamOwnerActor(auth);
  const actor = useMemo(() => buildComplianceConfigurationActor(auth), [auth]);
  const saveLockRef = useRef(false);

  const isAdd = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');

  useEffect(() => {
    if (!isAdd && !isEdit && ruleNameParam) {
      navigate(`${COMPLIANCE_CONFIGURATION_ROUTE}/${encodeURIComponent(ruleNameParam)}/edit`, {
        replace: true,
        state: location.state,
      });
    }
  }, [isAdd, isEdit, ruleNameParam, navigate, location.state]);

  const forms = useMemo(
    () => (Array.isArray(location.state?.forms) ? location.state.forms : buildSampleFormInventory()),
    [location.state?.forms],
  );

  const tradeCategories = useMemo(
    () => location.state?.tradeCategories ?? buildSavedTradeCategoryRecords(),
    [location.state?.tradeCategories],
  );

  const catalog = useMemo(() => {
    if (Array.isArray(location.state?.catalog) && location.state.catalog.length > 0) {
      return location.state.catalog;
    }
    return buildComplianceItemsCatalogFromLbt(location.state?.lbtSourceItems, forms, tradeCategories);
  }, [location.state?.catalog, location.state?.lbtSourceItems, forms, tradeCategories]);

  const [allConfigs, setAllConfigs] = useState(
    () => location.state?.configurations ?? buildSampleComplianceConfigurations(catalog, forms),
  );

  const resolvedConfig = useMemo(() => {
    if (isAdd) return null;
    const nameParam = decodeURIComponent(ruleNameParam || '');
    const fromState = location.state?.configuration;
    if (fromState && matchesUrlName(fromState.complianceItem, nameParam)) {
      return enrichConfigFromSource(fromState, findComplianceItem(catalog, fromState.complianceItemId));
    }
    const found = allConfigs.find((c) => matchesUrlName(c.complianceItem, nameParam));
    if (found) {
      return enrichConfigFromSource(found, findComplianceItem(catalog, found.complianceItemId));
    }
    return null;
  }, [isAdd, ruleNameParam, location.state?.configuration, allConfigs, catalog]);

  const [draft, setDraft] = useState(() =>
    isAdd ? createEmptyComplianceConfigurationDraft() : draftFromConfiguration(resolvedConfig),
  );
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveMessage, setSaveMessage] = useState('');
  const openedRuleFingerprintRef = useRef('');
  const openedSourceFingerprintRef = useRef('');

  useEffect(() => {
    if (Array.isArray(location.state?.configurations)) {
      setAllConfigs(location.state.configurations);
    }
  }, [location.state?.configurations]);

  useEffect(() => {
    if (isAdd) {
      const empty = createEmptyComplianceConfigurationDraft();
      setDraft(empty);
      setFieldErrors({});
      setSaveMessage('');
      openedRuleFingerprintRef.current = '';
      openedSourceFingerprintRef.current = '';
      return;
    }
    if (!resolvedConfig) return;
    const nextDraft = draftFromConfiguration(resolvedConfig);
    setDraft(nextDraft);
    setFieldErrors({});
    openedRuleFingerprintRef.current = ruleFingerprint(nextDraft);
    const source = findComplianceItem(catalog, nextDraft.complianceItemId);
    openedSourceFingerprintRef.current = sourceFingerprint(source);
  }, [isAdd, resolvedConfig, catalog]);

  const selectedCatalogItem = useMemo(() => {
    const id = draft.complianceItemId || resolvedConfig?.complianceItemId;
    return findComplianceItem(catalog, id);
  }, [draft.complianceItemId, resolvedConfig?.complianceItemId, catalog]);

  const linkedForm = useMemo(
    () => resolveLinkedForm(selectedCatalogItem, forms),
    [selectedCatalogItem, forms],
  );

  const conditionFields = useMemo(
    () => getConditionFieldsForSource(selectedCatalogItem, linkedForm),
    [selectedCatalogItem, linkedForm],
  );

  const fieldOptions = useMemo(
    () =>
      conditionFields.map((field) => ({
        id: field.id,
        name: field.fieldLabel,
      })),
    [conditionFields],
  );

  const applicationFields = useMemo(
    () => getSourceApplicationFields(selectedCatalogItem, linkedForm),
    [selectedCatalogItem, linkedForm],
  );

  const decisionChecklist = useMemo(() => getDecisionFormChecklist(forms), [forms]);

  const complianceType = selectedCatalogItem?.complianceType || resolvedConfig?.complianceType || '';
  const complianceItemName = selectedCatalogItem?.name || resolvedConfig?.complianceItem || '';
  const sourceSelected = Boolean(draft.complianceItemId || resolvedConfig?.complianceItemId);
  const sourceLocked = !isAdd && Boolean(resolvedConfig?.id);
  const conditions = draft.conditions || [];

  const availableComplianceItems = useMemo(() => {
    const includeId = isAdd ? undefined : resolvedConfig?.complianceItemId;
    return getUnlinkedComplianceItems(catalog, allConfigs, { includeItemId: includeId }).map((item) => ({
      id: item.id,
      name: formatComplianceSourceOptionLabel(item),
    }));
  }, [catalog, allConfigs, isAdd, resolvedConfig?.complianceItemId]);

  const tradeLabel = getRuleTradeCategoryLabel(
    {
      tradeCategoryIds: selectedCatalogItem?.tradeCategoryId
        ? [selectedCatalogItem.tradeCategoryId]
        : draft.tradeCategoryIds?.length
          ? draft.tradeCategoryIds
          : resolvedConfig?.tradeCategoryIds,
    },
    tradeCategories,
  );

  const validationMessages = useMemo(() => {
    const seen = new Set();
    const list = [];
    Object.values(fieldErrors || {}).forEach((message) => {
      if (!message || seen.has(message)) return;
      seen.add(message);
      list.push(message);
    });
    return list;
  }, [fieldErrors]);

  const clearError = (key) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSelectSource = (id) => {
    const item = findComplianceItem(catalog, id);
    setDraft((prev) => ({
      ...prev,
      complianceItemId: id,
      formId: item?.formId || '',
      complianceType: item?.complianceType || '',
      complianceItem: item?.name || '',
      description: item?.description || '',
      tradeCategoryIds: item?.tradeCategoryId ? [item.tradeCategoryId] : [],
    }));
    clearError('complianceItemId');
    openedSourceFingerprintRef.current = sourceFingerprint(item);
  };

  const handleAddCondition = () => {
    setDraft((prev) => ({
      ...prev,
      conditions: [
        ...(prev.conditions || []),
        createEmptyCondition({ id: nextConditionId(prev.conditions) }),
      ],
    }));
  };

  const handleUpdateCondition = (conditionId, patch) => {
    setDraft((prev) => ({
      ...prev,
      conditions: (prev.conditions || []).map((condition) => {
        if (condition.id !== conditionId) return condition;
        const next = { ...condition, ...patch };
        if (patch.fieldId) {
          const field = conditionFields.find((row) => row.id === patch.fieldId);
          next.fieldLabel = field?.fieldLabel || '';
          next.fieldControl = field?.fieldControl || '';
          const allowed = operatorsForKind(field?.kind).map((row) => row.value);
          if (next.operator && !allowed.includes(next.operator)) {
            next.operator = '';
          }
          next.value = '';
          next.values = [];
        }
        if (patch.operator) {
          next.value = '';
          next.values = [];
        }
        if (patch.values) {
          next.values = patch.values;
          next.value = patch.values[0] || '';
        } else if (patch.value != null) {
          next.values = String(patch.value || '').trim() ? [String(patch.value).trim()] : [];
        }
        return next;
      }),
    }));
  };

  const handleRemoveCondition = (conditionId) => {
    setDraft((prev) => ({
      ...prev,
      conditions: (prev.conditions || []).filter((condition) => condition.id !== conditionId),
    }));
  };

  const navigateToList = (nextConfigs, flashMessage) => {
    navigate(COMPLIANCE_CONFIGURATION_ROUTE, {
      state: {
        configurations: nextConfigs,
        catalog,
        forms,
        tradeCategories,
        ...(flashMessage ? { flashMessage } : {}),
      },
    });
  };

  const handleCancel = () => {
    navigateToList(allConfigs);
  };

  const handleSave = () => {
    if (saveLockRef.current) return;
    const result = mergeComplianceConfigurationsAfterSave(allConfigs, {
      configId: isAdd ? null : resolvedConfig?.id,
      draft: {
        ...draft,
        formId: selectedCatalogItem?.formId || draft.formId,
        complianceType,
        complianceItem: complianceItemName,
      },
      actor,
      catalog,
      forms,
      conditionFields,
      openedRuleFingerprint: openedRuleFingerprintRef.current,
      openedSourceFingerprint: openedSourceFingerprintRef.current,
      currentSourceFingerprint: sourceFingerprint(selectedCatalogItem),
    });

    if (!result.success) {
      setFieldErrors(result.errors || {});
      setSaveMessage(result.errors?.checksFailed || result.errors?.ruleChanged || result.errors?.sourceChanged || result.message || SAVE_FAILURE_MESSAGE);
      return;
    }
    saveLockRef.current = true;
    navigateToList(result.configs, result.unchanged ? undefined : result.message);
  };

  if (!canAccess) {
    return (
      <AdminLayout>
        <AdminPageTitle>Compliance</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          Configuration is developer-controlled. You do not have permission to view Compliance.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  if (!isAdd && !resolvedConfig) {
    return (
      <AdminLayout>
        <AdminPageTitle>Compliance configuration not found</AdminPageTitle>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={isAdd ? 'Add New' : 'Edit Compliance Rule'}
        onSubmit={handleSave}
        showSave={false}
      >
        {!isAdd ? (
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack !mb-0">Rule Details</AdminPanelTitle>
            <AdminConfigLockedField
              label="Compliance Rule ID"
              value={resolvedConfig?.id}
              mono
              isView={false}
            />
          </div>
        ) : null}

        <div className="space-y-4">
          <AdminPanelTitle className="text-offblack !mb-0">Rule Scope</AdminPanelTitle>

          {sourceLocked ? (
            <AdminConfigLockedField
              label="Source Form"
              value={formatComplianceSourceOptionLabel(selectedCatalogItem || {
                id: draft.complianceItemId,
                name: complianceItemName,
              })}
              isView={false}
            />
          ) : (
            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>
                Source Form <span className="text-alertred">*</span>
              </AdminDetailFieldLabel>
              <SearchableSelect
                value={draft.complianceItemId}
                options={availableComplianceItems}
                onChange={handleSelectSource}
                placeholder="Search Source Form"
                error={fieldErrors.complianceItemId}
              />
            </div>
          )}
          {sourceLocked && fieldErrors.complianceItemId ? (
            <AdminFormFieldError>{fieldErrors.complianceItemId}</AdminFormFieldError>
          ) : null}

          {sourceSelected ? (
            <>
              <AdminConfigLockedField
                label="Source ID"
                value={draft.complianceItemId || resolvedConfig?.complianceItemId}
                mono
                isView={false}
              />
              <SourceIconDisplay
                iconUrl={selectedCatalogItem?.iconUrl}
                fileName={selectedCatalogItem?.iconFileName}
              />
              <AdminConfigLockedField label="Name" value={complianceItemName || '—'} isView={false} />
              <AdminConfigLockedField label="Type" value={getComplianceTypeLabel(complianceType)} isView={false} />
              <AdminConfigLockedField
                label="Description"
                value={selectedCatalogItem?.description || resolvedConfig?.description || '—'}
                isView={false}
              />
              <AdminConfigLockedField label="Trade Category" value={tradeLabel || '—'} isView={false} />

              <div className="space-y-4">
                <AdminDetailFieldLabel className="!mb-0">
                  Form{linkedForm ? ` — ${linkedForm.formName}` : complianceItemName ? ` — ${complianceItemName} form` : ''}
                </AdminDetailFieldLabel>
                {applicationFields.length > 0 ? (
                  <SpecTable
                    columns={TABLE_3}
                    rows={[
                      ['Field', 'Type', 'Requirement'],
                      ...applicationFields.map((field) => [
                        field.fieldLabel || '—',
                        field.fieldControl || '—',
                        field.required === 'Required' ? 'Required' : 'Optional',
                      ]),
                    ]}
                  />
                ) : null}
              </div>
            </>
          ) : null}
        </div>

        <div className="space-y-4">
          <AdminPanelTitle className="text-offblack !mb-0">Rule Configuration</AdminPanelTitle>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <AdminDetailFieldLabel className="!mb-0">Optional Conditions</AdminDetailFieldLabel>
              <AdminSupportingText className="mb-0">
                {conditions.length ? `${conditions.length} added` : 'No Conditions'}
              </AdminSupportingText>
            </div>

            {conditions.map((condition, conditionIndex) => {
              const field = conditionFields.find((row) => row.id === condition.fieldId) || null;
              const operatorOptions = field ? operatorsForKind(field.kind) : CONDITION_OPERATOR_OPTIONS;
              const shownOperators =
                condition.operator && !operatorOptions.some((opt) => opt.value === condition.operator)
                  ? [...operatorOptions, { value: condition.operator, label: condition.operator }]
                  : operatorOptions;
              const valueOptions = field?.options || [];
              const useMultiEquals = field?.kind === 'multi' && condition.operator === 'equals';
              const useSelect =
                !useMultiEquals &&
                ((field?.kind === 'single' && valueOptions.length > 0) ||
                  (field?.kind === 'multi' && condition.operator === 'contains' && valueOptions.length > 0));
              const useDate = field?.kind === 'date';
              return (
                <div key={condition.id} className="rounded-lg border border-lightgray p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <AdminSupportingText className="mb-0 font-semibold !text-offblack">
                      Condition {conditionIndex + 1}
                    </AdminSupportingText>
                    <button
                      type="button"
                      onClick={() => handleRemoveCondition(condition.id)}
                      className={trashButtonClass}
                      aria-label={`Remove Condition ${conditionIndex + 1}`}
                    >
                      <i className="fi fi-rs-trash text-sm" aria-hidden />
                    </button>
                  </div>
                  <div className="space-y-1 min-w-0">
                    <AdminDetailFieldLabel>Field</AdminDetailFieldLabel>
                    <div className="relative w-full min-w-0">
                      <select
                        value={condition.fieldId || ''}
                        onChange={(e) =>
                          handleUpdateCondition(condition.id, { fieldId: e.target.value })
                        }
                        className={adminFormDetailSelectClasses(
                          false,
                          Boolean(fieldErrors[`conditions.${conditionIndex}`]),
                        )}
                      >
                        <option value="">Select Field</option>
                        {fieldOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.name}
                          </option>
                        ))}
                        {condition.fieldId &&
                        !fieldOptions.some((opt) => opt.id === condition.fieldId) ? (
                          <option value={condition.fieldId}>
                            {condition.fieldLabel || condition.lastAvailableLabel || condition.fieldId}
                          </option>
                        ) : null}
                      </select>
                      <AdminFormDropdownChevron />
                    </div>
                  </div>
                  <div className="space-y-1 min-w-0">
                    <AdminDetailFieldLabel>Operator</AdminDetailFieldLabel>
                    <div className="relative w-full min-w-0">
                      <select
                        value={condition.operator || ''}
                        onChange={(e) =>
                          handleUpdateCondition(condition.id, { operator: e.target.value })
                        }
                        className={adminFormDetailSelectClasses(false)}
                      >
                        <option value="">Select Operator</option>
                        {shownOperators.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <AdminFormDropdownChevron />
                    </div>
                  </div>
                  <div className="space-y-1 min-w-0">
                    <AdminDetailFieldLabel>Value</AdminDetailFieldLabel>
                    {useMultiEquals ? (
                      <MultiOptionList
                        options={valueOptions}
                        values={condition.values || []}
                        onChange={(values) => handleUpdateCondition(condition.id, { values })}
                      />
                    ) : useSelect ? (
                      <div className="relative w-full min-w-0">
                        <select
                          value={condition.value || ''}
                          onChange={(e) =>
                            handleUpdateCondition(condition.id, { value: e.target.value })
                          }
                          className={adminFormDetailSelectClasses(false)}
                        >
                          <option value="">Select Value</option>
                          {valueOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                          {condition.value && !valueOptions.includes(condition.value) ? (
                            <option value={condition.value}>{condition.value}</option>
                          ) : null}
                        </select>
                        <AdminFormDropdownChevron />
                      </div>
                    ) : (
                      <input
                        type={useDate ? 'date' : 'text'}
                        value={condition.value || ''}
                        onChange={(e) =>
                          handleUpdateCondition(condition.id, { value: e.target.value })
                        }
                        className={adminFormDetailInputClasses(false)}
                      />
                    )}
                    {fieldErrors[`conditions.${conditionIndex}`] ? (
                      <AdminFormFieldError>{fieldErrors[`conditions.${conditionIndex}`]}</AdminFormFieldError>
                    ) : null}
                  </div>
                </div>
              );
            })}

            {sourceSelected && conditionFields.length > 0 ? (
              <button type="button" onClick={handleAddCondition} className={addItemLinkClass}>
                <i className="fi fi-rs-plus text-xs" aria-hidden />
                Add Condition
              </button>
            ) : null}
          </div>

          <div className="space-y-3">
            <AdminConfigLockedField
              label="Decision Form"
              value={formatComplianceDecisionFormDisplay(forms)}
              isView={false}
            />
            <SpecTable
              columns={TABLE_2}
              rows={[
                ['Check', 'Mandatory'],
                ...decisionChecklist.map((check) => [
                  check.label,
                  check.mandatory ? 'Mandatory' : 'Optional',
                ]),
              ]}
            />
          </div>

          <div className="space-y-3">
            <AdminDetailFieldLabel className="!mb-0">Approval Requirements</AdminDetailFieldLabel>
            <SpecTable
              columns={TABLE_3}
              rows={[
                ['Case Type', 'Record requirement', 'Result on approval'],
                ...APPROVAL_REQUIREMENT_ROWS.map((row) => [row.caseType, row.record, row.result]),
              ]}
            />
            <AdminDetailFieldLabel className="!mb-0">Effective From</AdminDetailFieldLabel>
            <SpecTable
              columns={TABLE_2}
              rows={[
                ['Scenario', 'Effective From'],
                ...EFFECTIVE_FROM_ROWS.map((row) => [row.scenario, row.value]),
              ]}
            />
          </div>

          <div className="space-y-3">
            <AdminDetailFieldLabel className="!mb-0">Rejection Requirements</AdminDetailFieldLabel>
            <SpecTable
              columns="minmax(0, 1fr)"
              rows={[['Rejection Basis'], ...REJECTION_BASIS_OPTIONS.map((basis) => [basis])]}
            />
            <SpecTable
              columns={TABLE_2}
              rows={[
                ['Existing Business Compliance Record', 'Result on rejection'],
                ['No', 'No Record'],
                ['Yes', 'Keep Current Record'],
              ]}
            />
          </div>

          <div className="space-y-3">
            <AdminDetailFieldLabel className="!mb-0">Ask More Info</AdminDetailFieldLabel>
            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>
                Response Period <span className="text-alertred">*</span>
              </AdminDetailFieldLabel>
              <input
                type="text"
                inputMode="numeric"
                value={draft.responsePeriodDays ?? ''}
                onChange={(e) => {
                  setDraft((prev) => ({ ...prev, responsePeriodDays: e.target.value }));
                  clearError('responsePeriodDays');
                }}
                className={adminFormDetailInputClasses(false, Boolean(fieldErrors.responsePeriodDays))}
                aria-label="Response Period"
              />
              {fieldErrors.responsePeriodDays ? (
                <AdminFormFieldError>{fieldErrors.responsePeriodDays}</AdminFormFieldError>
              ) : null}
            </div>
            <SpecTable
              columns={TABLE_2}
              rows={[
                ['Item', 'Requirement'],
                ...ASK_MORE_INFO_FIXED_ROWS.map((row) => [row.label, row.value]),
              ]}
            />
          </div>
        </div>

        {validationMessages.length > 0 ? (
          <div className="space-y-3">
            <AdminPanelTitle className="text-offblack !mb-0">Validation Messages</AdminPanelTitle>
            {saveMessage ? <AdminFormFieldError className="mb-0">{saveMessage}</AdminFormFieldError> : null}
            {validationMessages.map((message) => (
              <AdminFormFieldError key={message} className="mb-0">
                {message}
              </AdminFormFieldError>
            ))}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <AdminFormSecondaryAction type="button" onClick={handleCancel}>
            Cancel
          </AdminFormSecondaryAction>
          <AdminHeaderAction type="submit">Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default ComplianceRule;
